import type {
  AnalyticsEvent,
  EventContext as ApmEvent,
} from '@frameio/telemetry';
import { emitAnalyticsEvent, emitEvent } from '@frameio/telemetry';
import type {
  UploadCanceled,
  UploadCompleted,
  UploadDetails,
  UploadFailed,
  UploadPaused,
  UploadRemoved,
  UploadResumed,
  UploadStarted,
  UploadTranscodeComplete,
} from '@frameio/telemetry/events/analytics/core-assets';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import type { TransferBatch } from '../types/uploader';
import type { UploadApolloEvents } from './useUploadingApollo';

type UploadApmEvents = {
  [key in UploadApolloEvents]?: ApmEvent;
};

type UploadAnalyticsEvents = {
  [key in UploadApolloEvents]?: AnalyticsEvent;
};

export const useUploaderTelemetry = () => {
  return {
    sendToTelemetry,
  };
};

const sendToTelemetry = (evt: MessageEvent<any>) => {
  const { eventType } = evt.data as { eventType: UploadApolloEvents };

  const jobDetails = getJobApmDetails(evt);
  const apmEvents: UploadApmEvents = {
    jobCreated: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'created',
      jobDetails,
    },
    jobTranscodesFinalizing: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'transcodes-finalizing',
      jobDetails,
    },
  };

  const details = getJobAnalyticsDetails(evt);
  const detailsObj = details ? { details } : {};
  const analyticsEvents: UploadAnalyticsEvents = {
    jobStarted: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'started',
      ...detailsObj,
    } as UploadStarted,
    jobPaused: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'paused',
      ...detailsObj,
    } as UploadPaused,
    jobResumed: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'resumed',
      ...detailsObj,
    } as UploadResumed,
    jobCanceled: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'canceled',
      ...detailsObj,
    } as UploadCanceled,
    jobCompleted: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'completed',
      ...detailsObj,
    } as UploadCompleted,
    jobRemoved: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'removed',
      ...detailsObj,
    } as UploadRemoved,
    jobFailed: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'failed',
      ...detailsObj,
    } as UploadFailed,
    jobTranscodesComplete: {
      name: CoreAssetsEventNames.UploadedAssets,
      status: 'transcodes-complete',
      ...detailsObj,
    } as UploadTranscodeComplete,
  };

  if (apmEvents[eventType]) {
    const { name, ...context } = apmEvents[eventType];
    emitEvent(name, context);
  }

  if (analyticsEvents[eventType]) {
    emitAnalyticsEvent(analyticsEvents[eventType], {
      apmPayload: { jobDetails },
    });
  }
};

const getJobApmDetails = (evt: MessageEvent<any>) => {
  // Let's assume all details are relevant to the APM, unless otherwise removed
  const { job } = Array.isArray(evt.data.payload)
    ? evt.data.payload[0]
    : evt.data.payload;
  if (!job || !job.id) return {};

  const jobDetails = { ...job };
  jobDetails.timeTakenMilli =
    Date.now() - (new Date(job.insertedAt) as any as number);

  jobDetails.memoryUsage = {};

  if (typeof window !== 'undefined' && (window?.performance as any)?.memory) {
    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = (
      window.performance as any
    ).memory;
    jobDetails.memoryUsage.usedJSHeapSize = usedJSHeapSize;
    jobDetails.memoryUsage.totalJSHeapSize = totalJSHeapSize;
    jobDetails.memoryUsage.jsHeapSizeLimit = jsHeapSizeLimit;
  }

  // Let's remove extra information since it can get large
  delete jobDetails.tasks;

  return jobDetails;
};

const getJobAnalyticsDetails = (
  evt: Record<string, any>
): UploadDetails | null => {
  // Let's be specific with what we send to analytics
  const { job }: { job: TransferBatch } = Array.isArray(evt.data.payload)
    ? evt.data.payload[0]
    : evt.data.payload;
  if (!job || !job.id) return null;

  const jobDetails: UploadDetails = {
    id: job.id,
    upload_type: job.resourceType?.toLowerCase(),
    project_id: job.projectId ?? undefined,
    started_at: job.insertedAt,
    job_duration_milliseconds:
      Date.now() - (new Date(job.insertedAt) as any as number),
    viewable_files_count: job.fileCounts?.viewable ?? undefined,
    created_folders_count: job.numOfCreatedFolders ?? undefined,
    failed_files_count: job.fileCounts?.failed ?? undefined,
    files_count: job.fileCounts?.total ?? undefined,
    root_files_count: job.topLevelFileCount ?? undefined,
    root_folders_count: job.topLevelFolderCount ?? undefined,
    total_bytes: job.progress?.bytesTotal || 0,
    bytes_sent: job.progress?.bytesSent || 0,
  };

  return jobDetails;
};
